import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Play } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, messageErreur } from "@/lib/api";
import { initiales } from "@/lib/format";
import { Barre, Bulle, Colonnes, Info, Ligne, Profil, Puces, Rond, Systeme, initialesDe } from "./Wa";

export const heureCourte = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "");
const duree = (s) => { const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); return h ? `${h} h ${String(m).padStart(2, "0")}` : `${m} min`; };

export const Briefing = ({ b }) => b && (
  <Bulle ia heure={heureCourte(b.created_at)} testId="wa-briefing">
    <p className="text-xs font-semibold at-ia">💬 Assistant · {b.titre}</p>
    <p className="mt-1">{b.synthese}</p>
    {b.priorites?.length > 0 && <ul className="mt-2 space-y-1">{b.priorites.map((x) => <li key={x.texte}>• {x.lien ? <Link to={x.lien} className="underline">{x.texte}</Link> : x.texte}</li>)}</ul>}
  </Bulle>
);

export const Accueil3Colonnes = ({ items, filtres, briefing, rendu, detail, titreListe = "Accueil", testId }) => {
  const [filtre, setFiltre] = useState("tout");
  const [choix, setChoix] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const visibles = items.filter((x) => filtre === "tout" || x.type === filtre);
  const sel = visibles.find((x) => x.id === choix) || visibles[0] || null;
  const ouvrir = (id) => { setChoix(id); setOuvert(true); };
  const liste = (
    <>
      <Barre titre={titreListe} testId={`${testId}-list-bar`} />
      <Puces items={filtres} valeur={filtre} onChange={setFiltre} testId={`${testId}-filters`} />
      <div className="overflow-y-auto flex-1 min-h-0 pb-24 md:pb-0" data-testid={`${testId}-list`}>
        {visibles.length === 0 && <p className="text-sm at-muted p-4">Rien ici pour le moment.</p>}
        {visibles.map((x) => <Ligne key={x.id} actif={sel?.id === x.id} onClick={() => ouvrir(x.id)} rond={<Rond texte={x.rond || initialesDe(x.titre)} couleur={x.couleur} />} titre={x.titre} sous={x.sous} heure={x.heure} badge={x.badge} alerte={x.badge === "!"} testId={`${testId}-row-${x.id}`} />)}
      </div>
    </>
  );
  const fil = (
    <>
      <Barre titre={sel?.titre || "Aujourd'hui"} sous={sel?.sous} onRetour={() => setOuvert(false)} gauche={sel && <Rond texte={sel.rond || initialesDe(sel.titre)} couleur={sel.couleur} />} testId={`${testId}-thread-bar`} />
      <div className="wa-defil-col flex flex-col gap-2" data-testid={`${testId}-thread`}>
        <Systeme>Aujourd'hui · {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</Systeme>
        <Briefing b={briefing} />
        {sel && rendu(sel)}
      </div>
    </>
  );
  return <Colonnes ouvert={ouvert} liste={liste} fil={fil} detail={detail} testId={testId} />;
};

const renduAgent = (x) => {
  if (x.type === "fiches") return (
    <>
      <Bulle choisie testId="wa-next-bubble">
        <p className="font-semibold">{x.p.civilite} {x.p.prenom} {x.p.nom}</p>
        <p className="at-muted text-[13px]">{x.p.ville} ({x.p.departement}) · Fichier de prospection</p>
        <p className="text-[13px] mt-1">{x.p.statut_nom} · tentative {x.p.tentatives + 1}{x.p.derniere_tentative ? ` · dernier appel le ${new Date(x.p.derniere_tentative).toLocaleDateString("fr-FR")}` : ""}</p>
      </Bulle>
      {x.conseil && <Bulle ia testId="next-card-tip"><p className="text-xs font-semibold at-ia">💡 Conseil de l'assistant</p><p className="mt-1">{x.conseil}</p></Bulle>}
      <div className="ml-2"><Link to={`/focus?prospect=${x.p.id}`} className="wa-avancer" data-testid="wa-call-button"><Phone size={14} />Appeler maintenant</Link></div>
    </>
  );
  return (
    <>
      <Bulle choisie testId="wa-item-bubble"><p className="font-semibold">{x.titre}</p><p className="text-[13px] at-muted">{x.texte || x.sous}</p></Bulle>
      {x.lien && <div className="ml-2"><Link to={x.lien} className="wa-avancer" data-testid="wa-item-open"><Play size={13} />{x.action || "Ouvrir"}</Link></div>}
    </>
  );
};

const itemsAgent = (d) => [
  ...(d.prochaine ? [{ id: "prochaine", type: "fiches", p: d.prochaine.prospect, conseil: d.prochaine.preparation, titre: `${d.prochaine.prospect.prenom} ${d.prochaine.prospect.nom}`, sous: `Prochaine fiche · ${d.prochaine.raison}`, badge: "1", couleur: "var(--primaire, var(--accent))" }] : []),
  ...d.suivants.map((p) => ({ id: p.id, type: "fiches", p, titre: `${p.prenom} ${p.nom}`, sous: `Ensuite · ${p.ville} · ${p.statut_nom}` })),
  ...d.rappels.map((t) => ({ id: t.id, type: "rappels", titre: t.titre, sous: "Rappel prévu", heure: heureCourte(t.echeance), lien: t.prospect_id ? `/focus?prospect=${t.prospect_id}` : `/fiches/${t.fiche_id}`, action: "Rappeler" })),
  ...d.manques.map((m) => ({ id: m.id, type: "a_traiter", titre: m.nom, sous: "Appel entrant manqué", heure: heureCourte(m.created_at), badge: "!", couleur: "var(--danger)", lien: `/focus?prospect=${m.prospect_id}`, action: "Rappeler" })),
  ...(d.annulations || []).map((a) => ({ id: a.id, type: "a_traiter", titre: a.fiche_nom, sous: "Dossier annulé", texte: a.motif, lien: `/fiches/${a.fiche_id}`, couleur: "var(--alerte, var(--danger))" })),
  ...d.fichiers.map((f) => ({ id: `fic-${f.id}`, type: "fichiers", titre: f.nom, sous: `${f.restants} contacts restants sur ${f.total}`, badge: f.restants || null, rond: "📂", texte: `${f.restants} contacts à appeler dans ce fichier.`, lien: "/appels", action: "Ouvrir mes appels" })),
];

const DerniersAppels = ({ appels }) => appels.slice(0, 3).map((a) => (
  <Bulle key={a.id} moi heure={heureCourte(a.created_at)} testId={`wa-my-call-${a.id}`}>📞 Appel à {a.nom} · {a.decroche ? `décroché (${duree(a.duree_s || 0)})` : "non décroché"}</Bulle>
));

export const AccueilAgentWa = () => {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [b, setB] = useState(null);
  useEffect(() => {
    api.get("/poste/tableau").then((r) => setD(r.data)).catch((e) => toast.error(messageErreur(e)));
    api.get("/assistant").then((r) => setB(r.data.briefing)).catch(() => {});
  }, []);
  if (!d) return <p className="at-muted p-6" data-testid="poste-loading">Chargement de votre accueil…</p>;
  const { compteur: c, objectifs: o, equipe: eq } = d;
  const detail = (
    <Profil initiales={initiales(user)} nom={`${user.prenom} ${user.nom}`} sous={`${user.role_label} · Ringover · simulation`} testId="wa-profile">
      <p className="at-label mb-1">Aujourd'hui</p>
      <Info label="📞 Appels passés" valeur={`${c.appels} / ${o.objectif_appels}`} testId="counter-calls" />
      <Info label="✅ Contacts joints" valeur={`${c.decroches} · ${c.appels ? Math.round((100 * c.decroches) / c.appels) : 0} %`} testId="counter-answered" />
      <Info label="🎯 Opportunités" valeur={`${c.opportunites} / ${o.objectif_opportunites}`} testId="counter-opportunities" />
      <Info label="⏱️ Temps en ligne" valeur={duree(d.temps_ligne_s)} testId="counter-online" />
      <p className="at-label mt-5 mb-1">Équipe</p>
      <Info label="Appels en moyenne" valeur={eq.appels_moyen} />
      <Info label="Taux de décroché" valeur={`${eq.taux_joint} %`} />
      <Link to="/focus" className="at-btn at-btn-primary w-full mt-5" data-testid="agent-hero-start"><Play size={16} />Démarrer ma session d'appels</Link>
    </Profil>
  );
  return (
    <div className="contents" data-testid="page-accueil">
      <Accueil3Colonnes testId="wa-home" items={itemsAgent(d)} briefing={b} detail={detail}
        filtres={[["tout", "Tout"], ["fiches", "Fiches"], ["rappels", "Rappels"], ["a_traiter", "À traiter"], ["fichiers", "Fichiers"]]}
        rendu={(x) => <><DerniersAppels appels={d.derniers_appels} />{renduAgent(x)}</>} />
    </div>
  );
};
